import { clamp } from "lodash-es";
import { GameEngine } from "~/engine/instance";
import { MAX_SMOKE_STALLED_TICKS } from "~/engine/sand";
import type { PlayerUpgradeValues, Vector2 } from "~/state/game-types";
import { TERRAIN_TYPES, VOXEL_EMPTY } from "~/terrain/types";
import type { Voxel } from "~/terrain/voxel-terrain";
import { mapNumber } from "~/utils/math";
import { randomChance } from "~/utils/random";

/**
 * Manages the mining mechanics
 */
export class MiningSystem {
  // private drillModifiedIndices: { [chunkId in number]?: Set<number> } = {};

  // Set default values
  private static fuelConsumptionRate = 0.01; // per drill action
  private static baseDrillTime = 500; // ms
  private static maxHeat = 100;
  private static maxFuel = 100;

  /** How long to wait after last drilling a voxel before decaying the drill progress */
  private static drillProgressDecayTime = 300;
  /** How fast the drill progress decays over time */
  private static drillProgressDecay = 2.0;

  private lastDrillTime = 0;

  /**
   * Called every frame while drilling. Handles progressive drilling.
   */
  private applyPlayerDrilling(deltaTime: number, voxelsInArea: Voxel[]) {
    const gameState = GameEngine.gameStore.getState();
    if (
      gameState.playerStats.fuel <= 0 ||
      gameState.playerStats.heat >= MiningSystem.maxHeat
    ) {
      return null;
    }

    const { totalResistance, totalHeatCause, collectedMinerals } =
      MiningSystem.applyDrilling(
        deltaTime,
        voxelsInArea,
        gameState.playerUpgrades.drillStrength,
      );

    if (collectedMinerals) {
      gameState.addToInventory(collectedMinerals);
    }

    // Apply fuel and heat cost per second (scaled by number of voxels being drilled)
    const fuelConsumption =
      MiningSystem.fuelConsumptionRate * deltaTime * totalResistance;
    const heatGeneration =
      (1.0 / gameState.playerUpgrades.heatResistance) *
      deltaTime *
      totalHeatCause;
    gameState.updatePlayerStats({
      fuel: clamp(
        gameState.playerStats.fuel - fuelConsumption,
        0,
        gameState.playerUpgrades.fuelCapacity,
      ),
      heat: clamp(
        gameState.playerStats.heat + heatGeneration,
        0,
        MiningSystem.maxHeat,
      ),
    });

    return voxelsInArea;
  }

  static applyDrilling(
    deltaTime: number,
    voxelsInArea: Voxel[],
    drillStrength: number,
  ) {
    const gameTime = GameEngine.gameLoop.gameTime;

    let totalResistance = 0;
    let totalHeatCause = 0;

    let collectedMinerals: { [id in number]: number } | undefined;

    // For each voxel in area, increment destruction
    for (const {
      indexInChunk,
      chunk,
      terrainInfo,
      // terrainType,
    } of voxelsInArea) {
      const { resistance, heatCause } = terrainInfo;

      if (resistance == null) continue;

      totalResistance += resistance;
      totalHeatCause += heatCause ?? resistance;

      const increment =
        (deltaTime * 1000) /
        ((MiningSystem.baseDrillTime / drillStrength) * resistance);

      const voxelStruct = chunk.voxelAt(indexInChunk);

      const newDestruction = voxelStruct.destruction + increment;
      voxelStruct.destruction = newDestruction;
      voxelStruct.lastDestructedAt = gameTime;

      if (newDestruction >= 1.0) {
        // collect minerals and remove voxel

        // Check for minerals
        collectedMinerals ||= {};
        collectedMinerals[terrainInfo.id] =
          (collectedMinerals[terrainInfo.id] ?? 0) + 1;

        // Remove the voxel (set to air)
        // or some chance of becoming smoke
        if (
          // has a chance of becoming smoke, unless the terrain-type cools you down
          (terrainInfo.heatCause ?? 0) < 0 &&
          randomChance(mapNumber(resistance, 0, 2, 0, 1, true))
        ) {
          voxelStruct.resetVoxel(TERRAIN_TYPES.smoke, true);

          // and make sure it de-spawns extra fast if it initially can't move
          voxelStruct.tiredness = (MAX_SMOKE_STALLED_TICKS * 0.75) | 0;
        } else {
          voxelStruct.resetVoxel(VOXEL_EMPTY, true);
        }
        // must mark as dirty before calling `updateVoxelChunk`
        chunk.needsRenderRect.addPointByIndex(indexInChunk);

        GameEngine.renderer.updateVoxelChunk(chunk);

        // this.drillProgressMap.delete(key);
      } else {
        GameEngine.renderer.setVoxelDestruction(
          chunk.id,
          indexInChunk,
          newDestruction,
        );

        chunk.needsDestructionDecay = true;
      }
    }

    return { totalResistance, totalHeatCause, collectedMinerals };
  }

  private getHoveredVoxels(
    miningMode: PlayerUpgradeValues["miningMode"],
    drillRadius: number,
    digPointerPosition: Vector2 | null,
  ): Voxel[] {
    if (digPointerPosition == null) return [];

    const player = GameEngine.player;
    if (!player) return [];

    let iterator;
    if (miningMode === "laser") {
      iterator = GameEngine.voxelTerrain.getVoxelsInLineRoundedCaps(
        player.getLaserOrigin(),
        digPointerPosition,
        drillRadius,
      );
    } else if (miningMode === "circle") {
      // i.e. circle mode
      iterator = GameEngine.voxelTerrain.getVoxelsInRadius(
        digPointerPosition,
        drillRadius,
      );
    } else {
      return [];
    }

    const voxelsInArea = [];
    for (const voxel of iterator) {
      const resistance = voxel.terrainInfo.resistance;
      if (resistance == null) continue; // unbreakable

      voxelsInArea.push(voxel);
    }

    return voxelsInArea;
  }

  /**
   * Passive update called each frame
   */
  public update(deltaTime: number): void {
    const gameState = GameEngine.gameStore.getState();
    const gameTime = GameEngine.gameLoop.gameTime;

    const playerPointerPosition = GameEngine.player?.pointerTarget || null;

    const hoveredVoxels = this.getHoveredVoxels(
      gameState.playerUpgrades.miningMode,
      gameState.playerUpgrades.drillRadius,
      playerPointerPosition,
    );

    GameEngine.renderer.setHoveredVoxels(hoveredVoxels);

    let drillingVoxels;
    if (
      hoveredVoxels.length > 0 &&
      GameEngine.inputManager.pointerPrimaryPressed
    ) {
      drillingVoxels = this.applyPlayerDrilling(deltaTime, hoveredVoxels);
    }

    GameEngine.player?.displayDrillingEffects(
      drillingVoxels ?? null,
      gameState.playerUpgrades.miningMode,
    );

    const timeSinceLastDrill = gameTime - this.lastDrillTime;
    if (drillingVoxels != null) {
      this.lastDrillTime = gameTime;
    }

    // Cool down the drill when not in use
    if (
      timeSinceLastDrill > MiningSystem.drillProgressDecayTime &&
      gameState.playerStats.heat > 0
    ) {
      const cooldownRate = gameState.playerUpgrades.cooldownRate * deltaTime;

      gameState.updatePlayerStats({
        heat: Math.max(0, gameState.playerStats.heat - cooldownRate),
      });
    }

    MiningSystem.cooldownVoxels(deltaTime);
  }

  static cooldownVoxels(deltaTime: number) {
    const gameTime = GameEngine.gameLoop.gameTime;

    // voxels that are no longer being drilled should decay their progress
    // over time, after `drillProgressDecayTime` has passed since last drilled
    // TODO: this seems like excess iterations for very few destructed voxels, can we have the GPU do this?
    //       or use a "dirty rect"?
    for (const chunk of GameEngine.voxelTerrain.chunks.values()) {
      if (chunk.voxelStructs == null) continue;

      if (!chunk.needsDestructionDecay) continue;

      const destructionBuffer = chunk.voxelStructs.buffers.destruction;
      const lastDestructedAtBuffer =
        chunk.voxelStructs.buffers.lastDestructedAt;

      let stableVoxelCount = 0;

      for (let i = 0, len = destructionBuffer.length; i < len; i++) {
        const destruction = destructionBuffer[i]!;
        if (destruction <= 0) {
          stableVoxelCount++;
          continue;
        }

        const timeSinceLastDrill = gameTime - lastDestructedAtBuffer[i]!;
        if (timeSinceLastDrill < MiningSystem.drillProgressDecayTime) continue;

        const voxelStruct = chunk.voxelAt(i);
        const newDestruction = Math.max(
          0,
          destruction - MiningSystem.drillProgressDecay * deltaTime,
        );
        voxelStruct.destruction = newDestruction;

        GameEngine.renderer.setVoxelDestruction(chunk.id, i, newDestruction);
      }

      if (stableVoxelCount === destructionBuffer.length) {
        chunk.needsDestructionDecay = false;
      }
    }
  }

  public dispose(): void {}
}
